// frontend/src/pages/Dashboard.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { databaseAPI } from '../services/api';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import NavBar from '../components/NavBar';

const API_URL = API_BASE_URL;

const Dashboard = () => {
  const [databases, setDatabases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [healthStatus, setHealthStatus] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDatabases = async () => {
      try {
        const response = await databaseAPI.list();
        setDatabases(response.databases);
        
        // Fetch health status for each database
        const healthResponse = await axios.get(`${API_URL}/health/connections`);
        if (healthResponse.data.success) {
          const healthMap = {};
          healthResponse.data.connections.forEach(conn => {
            healthMap[conn.id] = conn.health;
          });
          setHealthStatus(healthMap);
        }
      } catch (error) {
        console.error('Error fetching databases:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDatabases();
    
    // Refresh health status every 30 seconds
    const interval = setInterval(fetchDatabases, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleChat = (connectionId) => {
    navigate(`/chat/${connectionId}`);
  };

  const getHealthIcon = (health) => {
    if (!health) return '⚪';
    return health.status === 'healthy' ? '🟢' : '🔴';
  };

  const getHealthTooltip = (health) => {
    if (!health) return '';
    return health.status === 'healthy' 
      ? `Connected • ${health.total_queries || 0} queries`
      : health.error || 'Connection issue';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-950 via-purple-900 to-gray-950">
      <NavBar />
      
      <div className="max-w-7xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-bold mb-8 bg-gradient-to-r from-violet-400 to-purple-400 bg-clip-text text-transparent">
          Your Connected Databases
        </h1>
        
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500"></div>
            <p className="mt-4 text-gray-400">Loading databases...</p>
          </div>
        ) : databases.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-6xl mb-6">🗄️</div>
            <h2 className="text-2xl font-semibold mb-4 text-gray-300">No databases connected yet</h2>
            <p className="text-gray-500 mb-8">Connect a database or upload SQL files to get started</p>
            <div className="flex justify-center space-x-4">
              <button 
                onClick={() => navigate('/connect')}
                className="btn-primary"
              >
                <span className="mr-2">➕</span>
                Connect Database
              </button>
              {/* Hide Upload SQL Dump button temporarily
              <button 
                onClick={() => navigate('/upload')}
                className="btn-secondary"
              >
                <span className="mr-2">📤</span>
                Upload SQL Dump
              </button>
              */}
            </div>
          </div>
        ) : (
          <>
            <div className="flex justify-center space-x-4 mb-8">
              <button 
                onClick={() => navigate('/connect')}
                className="btn-primary"
              >
                <span className="mr-2">➕</span>
                Connect Database
              </button>
              {/* Hide Upload SQL Dump button temporarily
              <button 
                onClick={() => navigate('/upload')}
                className="btn-secondary"
              >
                <span className="mr-2">📤</span>
                Upload SQL Dump
              </button>
              */}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {databases.map((db) => (
              <div key={db.id} className="card-gradient p-6 hover:border-violet-700/50 transition-all duration-300">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-1">{db.name}</h3>
                    <p className="text-gray-400 text-sm">{db.type} • {db.host}</p>
                  </div>
                  <div className="relative group">
                    <span className="text-2xl cursor-help">{getHealthIcon(healthStatus[db.id])}</span>
                    <div className="absolute right-0 top-8 invisible group-hover:visible bg-gray-800 text-white text-xs rounded p-2 whitespace-nowrap z-10">
                      {getHealthTooltip(healthStatus[db.id])}
                    </div>
                  </div>
                </div>
                
                {healthStatus[db.id] && healthStatus[db.id].status === 'healthy' && (
                  <p className="text-xs text-gray-500 mb-4">
                    Last used: {new Date(healthStatus[db.id].last_used).toLocaleString()}
                  </p>
                )}
                
                <div className="flex space-x-2">
                  <button 
                    onClick={() => handleChat(db.id)}
                    disabled={healthStatus[db.id] && healthStatus[db.id].status !== 'healthy'}
                    className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all duration-200 ${
                      healthStatus[db.id] && healthStatus[db.id].status !== 'healthy'
                        ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                        : 'bg-violet-600 hover:bg-violet-700 text-white'
                    }`}
                  >
                    Chat with Database
                  </button>
                  {healthStatus[db.id] && healthStatus[db.id].status !== 'healthy' && (
                    <button 
                      onClick={() => window.location.reload()}
                      className="py-2 px-4 rounded-lg font-medium bg-red-600 hover:bg-red-700 text-white transition-all duration-200"
                    >
                      Reconnect
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;